import { Container, IDestroyOptions, ObservablePoint, Sprite, TextStyle, Text } from "pixi.js";
import { GameFont } from "by/gamefactory/cityversetycoon/scenes/common/GameFont";
import { LoginResourcePackage } from "../../popups/login/resources/LoginResourcePackage";
import { InputType } from "../../popups/login/InputType";
import { ValidationRule } from "../../popups/login/ValidationRule";
import { Event } from "event-system";
import { ICanvasScaler } from "../../../../canvas/ICanvasScaler";

export class LoginHtmlInput extends Container {
    private static readonly DEFAULT_TINT = "#ffffff";
    private static readonly YELLOW_TINT = "#FFE500";
    private static readonly RED_TINT = "#FF007A";

    public readonly onTextChanged: Event<string, InputType>;

    private readonly loginResources: LoginResourcePackage;
    private readonly canvasScaler: ICanvasScaler;
    private readonly isReadonly: boolean;

    private bg: Sprite;
    private input: HTMLInputElement;
    private validationFailedText: Text;
    private type: InputType;
    private validationRule: { regex: RegExp; validationFailedText: string };

    public get value(): string {
        return this.input.value;
    }
    public set value(value: string) {
        this.input.value = value;
    }

    public constructor(loginResources: LoginResourcePackage, canvasScaler: ICanvasScaler, type: InputType, isReadonly: boolean = false) {
        super();
        this.loginResources = loginResources;
        this.canvasScaler = canvasScaler;
        this.type = type;
        this.isReadonly = isReadonly;
        this.onTextChanged = new Event<string, InputType>();
        this.build();
        this.setValidationRule();
        const callback = this.transform.position.cb.bind(this.transform);
        this.transform.position = new ObservablePoint(() => {
            this.layout();
            callback();
        }, this, this.x, this.y);
        setTimeout(() => {
            this.layout();
        }, 20);
        this.canvasScaler.onResize.add(this.onResize, this);
        this.once("added", this.addedToStage);
    }

    private onResize() {
        this.layout();
    }

    private addedToStage(): void {
        this.validationFailedText.visible = false;
    }

    private build() {
        this.bg = new Sprite(this.loginResources.assets_views_login_text_field_frame_png);
        this.addChild(this.bg);
        this.createInputField();
        this.validationFailedText = new Text("ERROR", new ValidationFailedTextStyle());
        const padding = 10;
        this.validationFailedText.position.y = this.height + padding;
        this.addChild(this.validationFailedText);
    }

    private createInputField() {
        this.input = document.createElement('input');
        document.body.appendChild(this.input);
        this.input.setAttribute("inputmode", "text");
        if (this.isReadonly) {
            this.input.setAttribute("readonly", "readonly");
            this.input.style.color = LoginHtmlInput.YELLOW_TINT;
        }
        this.input.style.position = "absolute";
        this.input.style.left = `${this.getGlobalPosition().x}px`;
        this.input.style.top = `${this.getGlobalPosition().y}px`;
        const width = this.bg.width * 0.84;
        this.input.style.width = `${width}px`;
        this.input.style.height = `${this.bg.height}px`;
        this.input.style.fontFamily = GameFont.DEFAULT_FONT;
        this.input.style.border = "none";
        this.input.style.fontSize = "32px";
        this.input.style.border = "none";
        this.input.style.outline = "none";
        this.input.style.backgroundColor = "transparent";
        this.input.style.padding = `0`;
        this.input.style.margin = `0`;
        this.startEditing = this.startEditing.bind(this);
        this.stopEditing = this.stopEditing.bind(this);
        this.editing = this.editing.bind(this);
        this.input.addEventListener("focus", this.startEditing);
        this.input.addEventListener("blur", this.stopEditing);
        this.input.addEventListener("input", this.editing);
        this.setPlaceholderColor("#cdcdcd");
    }

    private setPlaceholderColor(color: string) {
        const styleElement = document.createElement("style");
        document.head.appendChild(styleElement);
        const dateNow = Date.now();
        const id = `wrapped-input-${dateNow}`;
        this.input.id = id;
        styleElement.sheet.insertRule(`#${id}::placeholder { color: ${color};  }`);
    }

    private setValidationRule(): void {
        switch (this.type) {
            case InputType.EMAIL:
                this.validationRule = ValidationRule.rules.email;
                this.input.placeholder = "EMAIL";
                break;
            case InputType.EMAIL_CODE:
                this.validationRule = ValidationRule.rules.emailCode;
                this.input.placeholder = "CODE";
                this.input.maxLength = 6;
                this.input.setAttribute("inputmode", "numeric");
                break;
            default:
                break;
        }
    }

    private startEditing(): void {
        this.validationFailedText.visible = false;
        this.bg.tint = 16777215;
        this.input.style.color = this.isReadonly ? LoginHtmlInput.YELLOW_TINT : LoginHtmlInput.DEFAULT_TINT;
    }

    private editing(): void {
        this.onTextChanged.emit(this.input.value, this.type);
    }

    private stopEditing(): void {
        //handle keyboard hidden
    }

    public validationFailed(errorText: string): void {
        this.bg.tint = LoginHtmlInput.RED_TINT;
        this.validationFailedText.text = errorText;
        this.validationFailedText.visible = true;
    }

    public isTextMatchingRegex(): boolean {
        if (this.input.value == undefined || this.input.value == "") {
            this.validationFailed("*FIELD CANNOT BE EMPTY");
            return false;
        }
        if (!this.validationRule.regex.test(this.input.value)) {
            this.validationFailed(this.validationRule.validationFailedText);
            return false;
        } else {
            if (this.type == InputType.EMAIL) {
                const splitResult = this.input.value.split("@");
                if (splitResult[0].length > 64 || splitResult[1].length > 253) {
                    this.validationFailed(this.validationRule.validationFailedText);
                    return false;
                }
            }
        }
        return true;
    }

    public hide(): void {
        this.input.style.display = "none";
    }

    public show(): void {
        this.input.style.display = "block";
    }

    public layout(): void {
        if (this.input != null) {
            const bgBounds = this.bg.getBounds();
            let bgGlobalHeight = bgBounds.height * 0.7;
            let bgGlobalWidth = bgBounds.width * 0.84;
            this.input.style.width = `${bgGlobalWidth}px`;
            this.input.style.height = `${bgGlobalHeight}px`;
            this.input.style.fontSize = `${bgBounds.height * 0.6}px`;
            this.input.style.left = `${this.getGlobalPosition().x + bgBounds.width * 0.08}px`;
            this.input.style.top = `${this.getGlobalPosition().y + (bgBounds.height - bgGlobalHeight) * 0.4}px`;
        }
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.canvasScaler.onResize.remove(this.onResize, this);
        if (this.input) {
            this.input.removeEventListener('focus', this.startEditing);
            this.input.removeEventListener('blur', this.stopEditing);
            this.input.removeEventListener('input', this.editing);
            this.input.remove();
            this.input = null;
        }
        super.destroy(options);
    }
}

class ValidationFailedTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "FF007A",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 32,
        }));
    }
}
